import React, { useState, useEffect, useCallback } from 'react';
import {
  Search, CreditCard, Gift, DollarSign, FileText, TrendingUp,
  Users, Clock, AlertTriangle, RefreshCw, Download, ChevronLeft, Zap,
  ChevronRight, Filter, Activity, User, ArrowUpRight
} from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

export default function AdminBilling() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [loadingAdjustments, setLoadingAdjustments] = useState(false);
  
  // Stats
  const [stats, setStats] = useState(null);
  
  // User search
  const [userSearch, setUserSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Transactions
  const [transactions, setTransactions] = useState([]);
  const [txTotal, setTxTotal] = useState(0);
  const [txPage, setTxPage] = useState(1);
  const [txHasMore, setTxHasMore] = useState(false);
  const [txFilters, setTxFilters] = useState({ method: '', status: '', plan: '', search: '' });
  
  // Adjustments
  const [adjustments, setAdjustments] = useState([]);
  const [adjTotal, setAdjTotal] = useState(0);
  const [adjPage, setAdjPage] = useState(1);
  const [adjHasMore, setAdjHasMore] = useState(false);
  const [adjFilters, setAdjFilters] = useState({ type: '', user_id: '' });
  
  // Quick action forms
  const [overridePlan, setOverridePlan] = useState('growth');
  const [overrideCycle, setOverrideCycle] = useState('monthly');
  const [overrideReason, setOverrideReason] = useState('');
  const [trialDays, setTrialDays] = useState(30);
  const [trialReason, setTrialReason] = useState('');
  const [creditAmount, setCreditAmount] = useState('');
  const [creditType, setCreditType] = useState('credit');
  const [creditReason, setCreditReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [creditSearchType, setCreditSearchType] = useState('searches');
  const [creditSearchAmount, setCreditSearchAmount] = useState('');
  const [creditSearchReason, setCreditSearchReason] = useState('');
  const [selectedUserUsage, setSelectedUserUsage] = useState(null);

  // Load stats
  const loadStats = useCallback(async () => {
    try {
      const res = await api.get('/api/admin/billing/stats');
      setStats(res.data);
    } catch {}
  }, []);

  // Load transactions
  const loadTransactions = useCallback(async (page = 1) => {
    setLoadingTransactions(true);
    try {
      const params = { page, limit: 20, ...txFilters };
      Object.keys(params).forEach(k => !params[k] && delete params[k]);
      const res = await api.get('/api/admin/billing/transactions', { params });
      setTransactions(res.data.transactions || []);
      setTxTotal(res.data.total);
      setTxHasMore(res.data.has_more);
      setTxPage(page);
    } catch {}
    finally { setLoadingTransactions(false); }
  }, [txFilters]);

  // Load adjustments
  const loadAdjustments = useCallback(async (page = 1) => {
    setLoadingAdjustments(true);
    try {
      const params = { page, limit: 20 };
      if (adjFilters.type) params.type = adjFilters.type;
      if (adjFilters.user_id) params.user_id = adjFilters.user_id;
      const res = await api.get('/api/admin/billing/adjustments', { params });
      setAdjustments(res.data.adjustments || []);
      setAdjTotal(res.data.total);
      setAdjHasMore(res.data.has_more);
      setAdjPage(page);
    } catch {}
    finally { setLoadingAdjustments(false); }
  }, [adjFilters]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { if (activeTab === 'transactions') loadTransactions(1); }, [activeTab, txFilters]);
  useEffect(() => { if (activeTab === 'adjustments') loadAdjustments(1); }, [activeTab, adjFilters]);

  // User search
  const searchUsers = async (term) => {
    setUserSearch(term);
    if (term.length < 2) { setSearchResults([]); return; }
    try {
      const res = await api.get('/api/auth/admin/users', { params: { search: term, limit: 8 } });
      setSearchResults(res.data.users || []);
    } catch {}
  };

  const selectUser = async (user) => {
    setSelectedUser(user);
    setUserSearch('');
    setSearchResults([]);
    // Load usage data for context
    try {
      const res = await api.get(`/api/admin/users/${user.id}/usage`);
      setSelectedUserUsage(res.data);
    } catch { setSelectedUserUsage(null); }
  };

  // Quick actions
  const handleOverride = async () => {
    if (!selectedUser) return toast.error('Select a user first');
    if (!overrideReason) return toast.error('Provide a reason');
    setActionLoading(true);
    try {
      await api.post('/api/admin/billing/override-plan', {
        user_id: selectedUser.id, plan: overridePlan, billing_cycle: overrideCycle, reason: overrideReason,
      });
      toast.success(`${selectedUser.email} moved to ${overridePlan}`);
      setOverrideReason(''); loadStats();
    } catch { toast.error('Failed'); }
    finally { setActionLoading(false); }
  };

  const handleExtendTrial = async () => {
    if (!selectedUser) return toast.error('Select a user first');
    setActionLoading(true);
    try {
      await api.post('/api/admin/billing/extend-trial', {
        user_id: selectedUser.id, days: trialDays, reason: trialReason || 'Admin extension',
      });
      toast.success(`Extended by ${trialDays} days`);
      setTrialReason(''); loadStats();
    } catch { toast.error('Failed'); }
    finally { setActionLoading(false); }
  };

  const handleAddCredits = async () => {
    if (!selectedUser) return toast.error('Select a user first');
    if (!creditSearchAmount || isNaN(creditSearchAmount)) return toast.error('Enter valid amount');
    setActionLoading(true);
    try {
      await api.post('/api/admin/billing/add-credits', {
        user_id: selectedUser.id, credit_type: creditSearchType, amount: parseInt(creditSearchAmount), reason: creditSearchReason || 'Admin credit',
      });
      toast.success(`Added ${creditSearchAmount} ${creditSearchType} credits to ${selectedUser.email}`);
      setCreditSearchAmount(''); setCreditSearchReason(''); loadStats();
      // Refresh usage display
      try {
        const res = await api.get(`/api/admin/users/${selectedUser.id}/usage`);
        setSelectedUserUsage(res.data);
      } catch {}
    } catch { toast.error('Failed'); }
    finally { setActionLoading(false); }
  };

  const handleCredit = async () => {
    if (!selectedUser) return toast.error('Select a user first');
    if (!creditAmount || isNaN(creditAmount)) return toast.error('Enter valid amount');
    setActionLoading(true);
    try {
      await api.post('/api/admin/billing/issue-credit', {
        user_id: selectedUser.id, amount: parseFloat(creditAmount), type: creditType, reason: creditReason,
      });
      toast.success(`$${creditAmount} ${creditType} issued to ${selectedUser.email}`);
      setCreditAmount(''); setCreditReason(''); loadStats();
    } catch { toast.error('Failed'); }
    finally { setActionLoading(false); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ', ';
  const formatTime = (d) => d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ', ';

  return (
    <div className="page-content" style={{ maxWidth: '100%' }}>
      <div className="billing-page">
        {/* Header */}
        <div className="billing-header">
          <h1><span style={{ fontSize: 24 }}>💳</span> Billing Admin</h1>
          <p>Manage plans, payments, and adjustments.</p>
        </div>

        {/* KPI Cards */}
        {stats && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 24 }}>
            <KpiCard icon={<DollarSign size={20} />} label="MRR" value={`$${stats.mrr?.toLocaleString()}`} color="var(--success)" />
            <KpiCard icon={<Users size={20} />} label="Paying Users" value={stats.paying_users} color="var(--accent-primary)" />
            <KpiCard icon={<TrendingUp size={20} />} label="ARPU" value={`$${stats.arpu}`} color="var(--accent-secondary)" />
            <KpiCard icon={<Clock size={20} />} label="Active Trials" value={stats.active_trials} color="var(--info)" />
            <KpiCard icon={<AlertTriangle size={20} />} label="Expiring Trials" value={stats.expiring_trials} color="var(--warning)" />
            <KpiCard icon={<DollarSign size={20} />} label="Pending Revenue" value={`$${stats.pending_revenue?.toLocaleString()}`} color="var(--error)" />
          </div>
        )}

        {/* User Search Bar */}
        <div className="billing-plan-card" style={{ marginBottom: 20 }}>
          <div style={{ position: 'relative' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--input-bg)', borderRadius: 10, padding: '10px 16px', border: '1px solid var(--border-primary)' }}>
              <Search size={16} color="var(--text-secondary)" />
              <input type="text" value={userSearch} onChange={(e) => searchUsers(e.target.value)}
                placeholder="Search user by name or email to manage billing..."
                style={{ flex: 1, border: 'none', background: 'none', color: 'var(--text-primary)', fontSize: 14, outline: 'none' }} />
            </div>
            {searchResults.length > 0 && (
              <div style={{ position: 'absolute', top: 50, left: 0, right: 0, background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 10, boxShadow: 'var(--shadow-lg)', zIndex: 50, maxHeight: 200, overflowY: 'auto' }}>
                {searchResults.map(u => (
                  <div key={u.id} onClick={() => selectUser(u)} style={{ padding: '12px 16px', cursor: 'pointer', borderBottom: '1px solid var(--border-primary)' }}
                    onMouseEnter={(e) => e.target.style.background = 'var(--bg-tertiary)'} onMouseLeave={(e) => e.target.style.background = 'transparent'}>
                    <strong style={{ color: 'var(--text-primary)' }}>{u.full_name || u.email}</strong>
                    <span style={{ color: 'var(--text-tertiary)', marginLeft: 8, fontSize: 12 }}>{u.email}</span>
                    <span style={{ float: 'right', padding: '2px 10px', borderRadius: 8, fontSize: 11, fontWeight: 600, background: 'var(--accent-glow)', color: 'var(--accent-primary)', textTransform: 'capitalize' }}>{u.plan}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {selectedUser && (
            <div style={{ marginTop: 12, padding: '14px 18px', background: 'var(--accent-glow)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: 'var(--accent-gradient)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 700, fontSize: 16 }}>
                  {(selectedUser.full_name || selectedUser.email || 'U')[0].toUpperCase()}
                </div>
                <div>
                  <div style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{selectedUser.full_name || selectedUser.email}</div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{selectedUser.email} · <span style={{ textTransform: 'capitalize' }}>{selectedUser.plan}</span> Plan</div>
                {selectedUserUsage && (
                  <div style={{ display: 'flex', gap: 16, marginTop: 6 }}>
                    <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                      🔍 {selectedUserUsage.usage?.searches?.used || 0}/{selectedUserUsage.usage?.searches?.limit || 0} searches
                    </span>
                    <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                      ⚡ {selectedUserUsage.usage?.ai_scores?.used || 0}/{selectedUserUsage.usage?.ai_scores?.limit || 0} AI scores
                    </span>
                  </div>
                )}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => navigate(`/dashboard/admin/users/${selectedUser.id}`)} className="auth-button auth-button-primary" style={{ padding: '8px 14px', fontSize: 12 }}>
                  <User size={13} /> View Profile
                </button>
                <button onClick={() => setSelectedUser(null)} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border-primary)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 12 }}>
                  ✕ Clear
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="billing-tabs">
          {[
            { id: 'dashboard', icon: <Activity size={14} />, label: 'Actions' },
            { id: 'transactions', icon: <DollarSign size={14} />, label: 'Transactions' },
            { id: 'adjustments', icon: <FileText size={14} />, label: 'Adjustments' },
          ].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`billing-tab ${activeTab === tab.id ? 'active' : ''}`}>
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {/* DASHBOARD / ACTIONS TAB */}
        {activeTab === 'dashboard' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16 }}>
            {/* Plan Override */}
            <div className="billing-plan-card">
              <h4 style={{ color: 'var(--text-primary)', fontWeight: 600, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                <CreditCard size={16} /> Plan Override
              </h4>
              <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                <select value={overridePlan} onChange={(e) => setOverridePlan(e.target.value)} className="filter-select">
                  <option value="free">Free</option><option value="starter">Starter</option>
                  <option value="growth">Growth</option><option value="pro">Pro</option>
                  <option value="enterprise">Enterprise</option>
                </select>
                <select value={overrideCycle} onChange={(e) => setOverrideCycle(e.target.value)} className="filter-select">
                  <option value="monthly">Monthly</option><option value="yearly">Yearly</option>
                </select>
              </div>
              <input value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} placeholder="Reason (required)" className="filter-input" style={{ marginBottom: 10, width: '100%' }} />
              <button onClick={handleOverride} disabled={actionLoading || !selectedUser} className="auth-button auth-button-primary" style={{ width: '100%', padding: '12px' }}>
                Apply Override
              </button>
            </div>

            {/* Trial Extension */}
            <div className="billing-plan-card">
              <h4 style={{ color: 'var(--text-primary)', fontWeight: 600, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Gift size={16} /> Trial Extension
              </h4>
              <div style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'center' }}>
                <input type="number" value={trialDays} onChange={(e) => setTrialDays(parseInt(e.target.value) || 0)} placeholder="Days" className="filter-input" style={{ width: 100 }} />
                <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>days</span>
              </div>
              <input value={trialReason} onChange={(e) => setTrialReason(e.target.value)} placeholder="Reason (optional)" className="filter-input" style={{ marginBottom: 10, width: '100%' }} />
              <button onClick={handleExtendTrial} disabled={actionLoading || !selectedUser} className="auth-button auth-button-primary" style={{ width: '100%', padding: '12px' }}>
                Extend Period
              </button>
            </div>

            {/* Add Search/AI Credits */}
            <div className="billing-plan-card">
              <h4 style={{ color: 'var(--text-primary)', fontWeight: 600, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Zap size={16} /> Add Credits
              </h4>
              <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                <select value={creditSearchType} onChange={(e) => setCreditSearchType(e.target.value)} className="filter-select">
                  <option value="searches">Searches</option>
                  <option value="ai_scores">AI Scores</option>
                </select>
                <input type="number" value={creditSearchAmount} onChange={(e) => setCreditSearchAmount(e.target.value)} placeholder="Amount" className="filter-input" style={{ flex: 1 }} />
              </div>
              <input value={creditSearchReason} onChange={(e) => setCreditSearchReason(e.target.value)} placeholder="Reason" className="filter-input" style={{ marginBottom: 10, width: '100%' }} />
              <p style={{ color: 'var(--text-tertiary)', fontSize: 11, marginBottom: 10, lineHeight: 1.5 }}>
                ⓘ Credits apply to the current billing period only. Lost on plan change or period reset.
              </p>
              <button onClick={handleAddCredits} disabled={actionLoading || !selectedUser} className="auth-button auth-button-primary" style={{ width: '100%', padding: '12px' }}>
              <p style={{ color: "var(--text-tertiary)", fontSize: 11, marginBottom: 10, lineHeight: 1.5 }}>
                ⓘ Credits apply to current billing period only. Lost on plan change or period reset.
              </p>
                Add {creditSearchType === 'searches' ? 'Search' : 'AI'} Credits
              </button>
            </div>

            {/* Credit / Debit */}
            <div className="billing-plan-card">
              <h4 style={{ color: 'var(--text-primary)', fontWeight: 600, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                <DollarSign size={16} /> Credit / Debit
              </h4>
              <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                <input type="number" value={creditAmount} onChange={(e) => setCreditAmount(e.target.value)} placeholder="Amount ($)" className="filter-input" style={{ flex: 1 }} />
                <select value={creditType} onChange={(e) => setCreditType(e.target.value)} className="filter-select">
                  <option value="credit">Credit</option><option value="debit">Debit</option>
                  <option value="refund">Refund</option>
                </select>
              </div>
              <input value={creditReason} onChange={(e) => setCreditReason(e.target.value)} placeholder="Reason" className="filter-input" style={{ marginBottom: 10, width: '100%' }} />
              <button onClick={handleCredit} disabled={actionLoading || !selectedUser} className="auth-button auth-button-primary" style={{ width: '100%', padding: '12px' }}>
                Issue {creditType}
              </button>
            </div>
          </div>
        )}

        {/* TRANSACTIONS TAB */}
        {activeTab === 'transactions' && (
          <>
            {/* Filters */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
              <select value={txFilters.method} onChange={(e) => setTxFilters({...txFilters, method: e.target.value})} className="filter-select">
                <option value="">All Methods</option><option value="card">Card</option><option value="crypto">Crypto</option>
              </select>
              <select value={txFilters.status} onChange={(e) => setTxFilters({...txFilters, status: e.target.value})} className="filter-select">
                <option value="">All Status</option><option value="successful">Successful</option><option value="pending">Pending</option><option value="failed">Failed</option>
              </select>
              <select value={txFilters.plan} onChange={(e) => setTxFilters({...txFilters, plan: e.target.value})} className="filter-select">
                <option value="">All Plans</option><option value="starter">Starter</option><option value="growth">Growth</option><option value="pro">Pro</option><option value="enterprise">Enterprise</option>
              </select>
              <input value={txFilters.search} onChange={(e) => setTxFilters({...txFilters, search: e.target.value})} placeholder="Search by email..." className="filter-input" style={{ minWidth: 180 }} />
              <span style={{ color: 'var(--text-tertiary)', fontSize: 12, alignSelf: 'center', marginLeft: 'auto' }}>{txTotal} transactions</span>
            </div>

            {/* Table */}
            <div className="billing-history-card">
              <table className="billing-table">
                <thead>
                  <tr><th>Date</th><th>User</th><th>Plan</th><th>Amount</th><th>Method</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {loadingTransactions ? (
                    <tr><td colSpan={6} style={{ textAlign: 'center', padding: 30 }}><div className="loader" /></td></tr>
                  ) : transactions.length === 0 ? (
                    <tr><td colSpan={6} className="billing-empty">No transactions found.</td></tr>
                  ) : (
                    transactions.map(tx => (
                      <tr key={`${tx.method}-${tx.id}`}>
                        <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{formatTime(tx.created_at)}</td>
                        <td>
                          <span style={{ color: 'var(--accent-primary)', cursor: 'pointer' }} onClick={() => navigate(`/dashboard/admin/users/${tx.user_id}`)}>
                            {tx.user_email}
                          </span>
                        </td>
                        <td style={{ textTransform: 'capitalize' }}>{tx.plan}</td>
                        <td className="amount">${tx.amount?.toFixed(2)}</td>
                        <td>
                          <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: tx.method === 'card' ? 'var(--info-bg)' : 'var(--warning-bg)', color: tx.method === 'card' ? 'var(--info)' : 'var(--warning)' }}>
                            {tx.method === 'card' ? '💳 Card' : '₮ Crypto'}
                          </span>
                        </td>
                        <td>
                          <span className={`status-badge ${tx.status}`}>{tx.status}</span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {txTotal > 20 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 16 }}>
                <button onClick={() => loadTransactions(txPage - 1)} disabled={txPage === 1} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border-primary)', background: 'transparent', color: txPage === 1 ? 'var(--text-tertiary)' : 'var(--text-secondary)', cursor: txPage === 1 ? 'default' : 'pointer' }}>
                  <ChevronLeft size={16} /> Prev
                </button>
                <span style={{ color: 'var(--text-secondary)', fontSize: 13, alignSelf: 'center' }}>Page {txPage}</span>
                <button onClick={() => loadTransactions(txPage + 1)} disabled={!txHasMore} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border-primary)', background: 'transparent', color: txHasMore ? 'var(--text-secondary)' : 'var(--text-tertiary)', cursor: txHasMore ? 'pointer' : 'default' }}>
                  Next <ChevronRight size={16} />
                </button>
              </div>
            )}
          </>
        )}

        {/* ADJUSTMENTS TAB */}
        {activeTab === 'adjustments' && (
          <>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
              <select value={adjFilters.type} onChange={(e) => setAdjFilters({...adjFilters, type: e.target.value})} className="filter-select">
                <option value="">All Types</option><option value="credit">Credit</option><option value="debit">Debit</option><option value="refund">Refund</option>
              </select>
              <span style={{ color: 'var(--text-tertiary)', fontSize: 12, alignSelf: 'center', marginLeft: 'auto' }}>{adjTotal} adjustments</span>
            </div>

            <div className="billing-history-card">
              <table className="billing-table">
                <thead>
                  <tr><th>Date</th><th>User</th><th>Admin</th><th>Type</th><th>Amount</th><th>Reason</th></tr>
                </thead>
                <tbody>
                  {loadingAdjustments ? (
                    <tr><td colSpan={6} style={{ textAlign: 'center', padding: 30 }}><div className="loader" /></td></tr>
                  ) : adjustments.length === 0 ? (
                    <tr><td colSpan={6} className="billing-empty">No adjustments found.</td></tr>
                  ) : (
                    adjustments.map(adj => (
                      <tr key={adj.id}>
                        <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{formatTime(adj.created_at)}</td>
                        <td style={{ color: 'var(--accent-primary)', cursor: 'pointer' }} onClick={() => navigate(`/dashboard/admin/users/${adj.user_id}`)}>{adj.user_email}</td>
                        <td style={{ color: 'var(--text-secondary)' }}>{adj.admin_email}</td>
                        <td><span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: adj.type === 'credit' ? 'var(--success-bg)' : adj.type === 'refund' ? 'var(--warning-bg)' : 'var(--error-bg)', color: adj.type === 'credit' ? 'var(--success)' : adj.type === 'refund' ? 'var(--warning)' : 'var(--error)', textTransform: 'capitalize' }}>{adj.type}</span></td>
                        <td className="amount">${adj.amount?.toFixed(2)}</td>
                        <td style={{ color: 'var(--text-secondary)', fontSize: 12, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{adj.reason || ', '}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {adjTotal > 20 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 16 }}>
                <button onClick={() => loadAdjustments(adjPage - 1)} disabled={adjPage === 1} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border-primary)', background: 'transparent', color: adjPage === 1 ? 'var(--text-tertiary)' : 'var(--text-secondary)', cursor: adjPage === 1 ? 'default' : 'pointer' }}>
                  <ChevronLeft size={16} /> Prev
                </button>
                <span style={{ color: 'var(--text-secondary)', fontSize: 13, alignSelf: 'center' }}>Page {adjPage}</span>
                <button onClick={() => loadAdjustments(adjPage + 1)} disabled={!adjHasMore} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border-primary)', background: 'transparent', color: adjHasMore ? 'var(--text-secondary)' : 'var(--text-tertiary)', cursor: adjHasMore ? 'pointer' : 'default' }}>
                  Next <ChevronRight size={16} />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// KPI Card component
function KpiCard({ icon, label, value, color }) {
  return (
    <div className="billing-plan-card" style={{ textAlign: 'center', padding: 'var(--space-lg)' }}>
      <div style={{ color, marginBottom: 6, display: 'flex', justifyContent: 'center' }}>{icon}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-primary)' }}>{value}</div>
      <div style={{ color: 'var(--text-secondary)', fontSize: 11, marginTop: 2 }}>{label}</div>
    </div>
  );
}

